import { Award, Users, TrendingUp, Clock, Shield, Target, CheckCircle, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import OptimizedImage from './OptimizedImage';
import SEO from './SEO';
import { pageSEO } from '../seoData';

const About = () => {
  const milestones = [
    {
      year: '1996',
      title: 'Company Founded',
      description: 'Rumtek Finvest Pvt. Ltd. started operations'
    },
    {
      year: '2003',
      title: 'NSE/BSE Partnership',
      description: 'Became the master branch and channel partner of Sunidhi Securities, a member of NSE/BSE/MCX/NCDEX'
    },
    {
      year: '2005',
      title: 'Network Expansion',
      description: 'Appointed a network of 30 sub-brokers in and around Kolkata'
    },
    {
      year: '2009',
      title: 'NRI Division Launch',
      description: 'Started the NRI division for advising NRI clients across the globe'
    },
    {
      year: '2010',
      title: 'Mutual Fund Distribution',
      description: 'Started Mutual Fund distribution'
    },
    {
      year: '2015',
      title: 'Currency & Commodity Trading',
      description: 'Developed a client base for currency and commodity trading'
    },
    {
      year: '2020',
      title: 'Client Base Milestone',
      description: 'Achieved a satisfied client base of 2000 clients'
    },
    {
      year: '2022',
      title: 'Options Trading Advisory',
      description: 'Advising clients on option trading to create steady returns'
    },
    {
      year: '2023',
      title: 'Bond Advisory Services',
      description: 'Advising clients on Bonds'
    },
    {
      year: '2024',
      title: 'AUM Growth',
      description: 'Managing and advising clients on an AUM of ₹500 crores'
    },
    {
      year: '2025',
      title: 'PMS Distribution',
      description: 'Started full-fledged distribution of Portfolio Management Services (PMS) of different fund managers'
    }
  ];
  
  const values = [
    {
      icon: Shield,
      title: 'Trust & Transparency',
      description: 'We believe in complete transparency in all our dealings and maintaining the highest standards of trust with our clients.'
    },
    {
      icon: Target,
      title: 'Client-First Approach',
      description: 'Every investment recommendation is made with our clients\' financial goals and risk tolerance as the primary consideration.'
    },
    {
      icon: TrendingUp,
      title: 'Long-term Vision',
      description: 'We focus on sustainable wealth creation through long-term investment strategies rather than short-term gains.'
    },
    {
      icon: Award,
      title: 'Excellence in Service',
      description: 'Our commitment to excellence drives us to continuously improve our services and deliver superior client experiences.'
    }
  ];
  
  const teamHighlights = [
    {
      metric: '25+',
      label: 'Years Experience',
      description: 'Combined team experience in financial markets'
    },
    {
      metric: '5000+',
      label: 'Happy Clients',
      description: 'Satisfied investors trusting our guidance'
    },
    {
      metric: '₹500 Cr',
      label: 'Assets Under Management',
      description: 'Successfully managing diverse portfolios'
    },
    {
      metric: '98%',
      label: 'Client Retention',
      description: 'Long-term relationships built on trust'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEO {...pageSEO.about} />
      {/* Hero Section - Light & Clean */}
      <section className="relative bg-gradient-to-br from-blue-50 via-white to-purple-50 pt-20 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left Content */}
            <div>
              <div className="mb-6">
                <span className="inline-block bg-blue-600 text-white px-4 py-2 rounded-full text-sm font-semibold tracking-wide">
                  TRUSTED SINCE 1996
                </span>
              </div>
              <h1 className="text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight" data-testid="about-title">
                A Legacy of
                <span className="block bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                  25+ Years
                </span>
              </h1>
              <p className="text-xl text-gray-600 mb-8 leading-relaxed" data-testid="about-description">
                Since 1996, Rumtek Finvest has been a trusted investment advisor India and NRI investment consultant, 
                helping individuals and families achieve their financial dreams through 
                strategic wealth management India and expert financial planning services. As a leading mutual fund advisor Kolkata,
                we specialize in retirement planning NRI and portfolio management India solutions.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 mb-8">
                <Link
                  to="/services"
                  className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-8 py-4 rounded-xl font-bold text-lg hover:from-blue-700 hover:to-purple-700 transition-all transform hover:scale-105 inline-flex items-center justify-center shadow-xl"
                >
                  Explore Our Services
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
                <Link
                  to="/contact"
                  className="border-2 border-blue-600 text-blue-600 hover:bg-blue-600 hover:text-white px-8 py-4 rounded-xl font-bold text-lg transition-all inline-flex items-center justify-center"
                >
                  Get in Touch
                </Link>
              </div>
            </div>
            
            {/* Right Image */}
            <div className="relative">
              <div className="relative rounded-3xl overflow-hidden shadow-2xl">
                <OptimizedImage 
                  src="https://images.unsplash.com/photo-1701813937533-30028402cbc5?w=800&q=85" 
                  alt="Financial advisor India heritage and innovation - 25+ years trusted investment advisory with SEBI registration serving 5000+ clients with ₹500 Cr+ AUM"
                  className="w-full h-[500px]"
                  priority={true}
                  objectFit="cover"
                />
                <div className="absolute inset-0 bg-gradient-to-tr from-blue-600/10 to-purple-600/10"></div>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Mission & Vision Section */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4" data-testid="mission-vision-title">
              Our Mission & Vision
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Guided by our core values and commitment to excellence, we strive to be the most trusted investment partner for our clients.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Mission Card */}
            <div className="bg-white rounded-3xl p-8 shadow-xl hover:shadow-2xl transition-all transform hover:-translate-y-2" data-testid="mission-section">
              <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center mb-6">
                <Target className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-3xl font-bold text-gray-900 mb-4">Our Mission</h3>
              <p className="text-lg text-gray-700 leading-relaxed mb-4">
                To create wealth for all our clients through systematic investment planning and 
                diligent portfolio management. We are committed to putting your money to work 
                by investing diligently, with growth and capital protection always at the forefront 
                of our strategy.
              </p>
              <ul className="space-y-3">
                <li className="flex items-start text-gray-700">
                  <CheckCircle className="h-5 w-5 text-green-600 mr-3 flex-shrink-0 mt-1" />
                  <span>Deliver transparent, client-focused financial advice</span>
                </li>
                <li className="flex items-start text-gray-700">
                  <CheckCircle className="h-5 w-5 text-green-600 mr-3 flex-shrink-0 mt-1" />
                  <span>Create sustainable long-term wealth for all investors</span>
                </li>
                <li className="flex items-start text-gray-700">
                  <CheckCircle className="h-5 w-5 text-green-600 mr-3 flex-shrink-0 mt-1" />
                  <span>Maintain highest standards of integrity and trust</span>
                </li>
              </ul>
            </div>

            {/* Vision Card */}
            <div className="bg-white rounded-3xl p-8 shadow-xl hover:shadow-2xl transition-all transform hover:-translate-y-2" data-testid="vision-section">
              <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-purple-600 rounded-2xl flex items-center justify-center mb-6">
                <Award className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-3xl font-bold text-gray-900 mb-4">Our Vision</h3>
              <p className="text-lg text-gray-700 leading-relaxed mb-4">
                To be the most trusted investment partner, helping clients achieve financial 
                freedom through strategic asset allocation and personalized wealth management 
                solutions. We envision a future where every client can confidently pursue 
                their financial goals with our expert guidance.
              </p>
              <ul className="space-y-3">
                <li className="flex items-start text-gray-700">
                  <CheckCircle className="h-5 w-5 text-green-600 mr-3 flex-shrink-0 mt-1" />
                  <span>Expand reach to serve 10,000+ families by 2030</span>
                </li>
                <li className="flex items-start text-gray-700">
                  <CheckCircle className="h-5 w-5 text-green-600 mr-3 flex-shrink-0 mt-1" />
                  <span>Leverage technology for superior client experience</span>
                </li>
                <li className="flex items-start text-gray-700">
                  <CheckCircle className="h-5 w-5 text-green-600 mr-3 flex-shrink-0 mt-1" />
                  <span>Set industry benchmarks in financial advisory</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>
      
      {/* Leadership Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4" data-testid="leadership-title">
              Leadership
            </h2>
            <p className="text-xl text-gray-600">
              Meet the visionary behind Rumtek Finvest
            </p>
          </div>
          
          <div className="max-w-4xl mx-auto">
            <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-3xl p-8 lg:p-12 shadow-xl" data-testid="leadership-profile">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-center">
                <div className="text-center md:text-left">
                  <OptimizedImage 
                    src="https://customer-assets.emergentagent.com/job_wealth-hub-10/artifacts/zj0579m7_PixelDreams_BNI_12th_May0007.jpg" 
                    alt="Mr. Jaydip K Mehta - Founder and Managing Director of Rumtek Finvest, SEBI registered financial advisor with 25+ years expertise in wealth management India"
                    className="w-48 h-48 rounded-full mx-auto md:mx-0 shadow-2xl border-4 border-white"
                    objectFit="cover"
                  />
                </div>
                
                <div className="md:col-span-2">
                  <h3 className="text-3xl font-bold text-gray-900 mb-2">Mr. Jaydip K Mehta</h3>
                  <p className="text-lg text-blue-600 font-semibold mb-4">Managing Director & Founder</p>
                  <p className="text-gray-700 leading-relaxed mb-4">
                    With over 25 years of experience in the financial markets, Mr. Jaydip K Mehta 
                    founded Rumtek Finvest with a simple yet powerful vision: to democratize wealth 
                    creation and make professional investment management accessible to all.
                  </p>
                  <p className="text-gray-700 leading-relaxed">
                    Under his leadership, Rumtek has grown from a small investment advisory firm 
                    to managing over ₹500 crores in client assets, serving more than 5000 satisfied 
                    clients across various investment instruments.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Core Values */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4" data-testid="values-title">
              Our Core Values
            </h2>
            <p className="text-xl text-gray-600">
              The principles that guide every decision and interaction at Rumtek Finvest
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => {
              const Icon = value.icon;
              const colors = [
                'from-blue-500 to-blue-600',
                'from-green-500 to-green-600',
                'from-purple-500 to-purple-600',
                'from-orange-500 to-orange-600'
              ];
              
              return (
                <div key={index} className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all transform hover:-translate-y-2" data-testid={`value-${index}`}>
                  <div className={`w-16 h-16 bg-gradient-to-br ${colors[index]} rounded-2xl flex items-center justify-center mb-6`}>
                    <Icon className="h-8 w-8 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{value.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{value.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>
      
      {/* Our Journey Timeline */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4" data-testid="journey-title">
              Our Journey
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              From humble beginnings to managing ₹500+ crores - milestones that shaped our success
            </p>
          </div>
          
          <div className="relative">
            {/* Timeline Line */}
            <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-gradient-to-b from-blue-600 via-purple-600 to-blue-600 hidden lg:block"></div>
            
            <div className="space-y-8">
              {milestones.map((milestone, index) => (
                <div key={index} className="relative flex items-start lg:pl-20" data-testid={`milestone-${index}`}>
                  {/* Year Badge */}
                  <div className="absolute left-0 lg:left-4 w-16 h-16 bg-gradient-to-br from-blue-600 to-purple-600 rounded-2xl flex items-center justify-center shadow-lg">
                    <span className="text-white font-bold text-sm">{milestone.year}</span>
                  </div>
                  
                  {/* Content Card */}
                  <div className="ml-20 lg:ml-8 flex-1 bg-gradient-to-br from-blue-50 to-purple-50 rounded-2xl p-6 hover:shadow-xl transition-all transform hover:-translate-y-1">
                    <h3 className="text-xl font-bold text-gray-900 mb-2">{milestone.title}</h3>
                    <p className="text-gray-700">{milestone.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
      
      {/* Stats Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {teamHighlights.map((highlight, index) => (
              <div key={index} className="text-center p-6 rounded-2xl bg-gradient-to-br from-blue-50 to-purple-50 hover:shadow-lg transition-shadow" data-testid={`highlight-${index}`}>
                <div className="text-4xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-2">
                  {highlight.metric}
                </div>
                <div className="text-lg font-semibold text-gray-900 mb-1">{highlight.label}</div>
                <div className="text-sm text-gray-600">{highlight.description}</div>
              </div>
            ))}
          </div>
        </div>
      </section>
      
      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-blue-900 via-purple-900 to-blue-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl lg:text-5xl font-bold text-white mb-6" data-testid="cta-title">
            Ready to Begin Your Wealth Creation Journey?
          </h2>
          <p className="text-xl text-blue-100 mb-10 leading-relaxed">
            Join thousands of satisfied clients who have trusted us with their financial future. 
            Let our 25+ years of experience work for you.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              data-testid="schedule-consultation-btn"
              className="bg-white text-blue-900 px-10 py-5 rounded-xl font-bold text-lg hover:bg-blue-50 transition-all transform hover:scale-105 inline-flex items-center justify-center shadow-2xl"
            >
              Book Free Consultation
              <ArrowRight className="ml-2 h-6 w-6" />
            </Link>
            <Link
              to="/services"
              data-testid="explore-investments-btn"
              className="border-2 border-white text-white hover:bg-white hover:text-blue-900 px-10 py-5 rounded-xl font-bold text-lg transition-all inline-flex items-center justify-center"
            >
              View Our Services
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;